import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dropdown,
  Option,
  Label,
  MenuItem,
  Skeleton,
  SkeletonItem,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
} from '@fluentui/react-components';
import { ArrowSortDownRegular, ArrowSortUpRegular, SearchRegular, ArrowClockwiseRegular, AddRegular, DeleteRegular, DismissRegular, ErrorCircleRegular, Apps16Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAppsInProject } from '@/hooks/useApps';
import { useIsProjectOwner, usePermission } from '@/hooks/useUserPermissions';
import { syncOnboardingFromData, shouldShowOnboarding } from '@/hooks/useOnboardingProgress';
import { OnboardingChecklist } from '@/components/Onboarding';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ProjectOverviewHeader } from '@/components/shared/ProjectOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';
import { useTelemetry } from '@/hooks/useTelemetry';
import { visuallyHiddenStyle } from '@/styles/a11y';
import { paths } from '@/routes';
import { CreateAppDialog } from '@/components/Application/CreateAppDialog';
import { CREATE_FLAG_PARAM } from '@/routes/queryParams';
import { formatDateTime } from '@/utils/dateTime';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useRecentWorkStore } from '@/stores/recentWorkStore';
import { useAuth } from '@/auth/useAuth';
import type { AutomationAppUIModel } from '@/types/automation';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { DeleteConfirmDialog } from '@/components/common/DeleteConfirmDialog';
import { deleteAutomationApp } from '@/services/projectService';
import {
  EntityNameCell,
  RowActionsMenu,
  TablePaginationFooter,
  DataTablePanel,
  DotStatusBadge,
  usePagination,
  type DotStatusKind,
} from '@/components/shared/DataTable';

type AppStatus = 'Ready' | 'Provisioning' | 'Deleting' | 'Failed' | 'NotReady';

const getAppStatus = (app: AutomationAppUIModel): AppStatus => {
  const state = app.properties?.provisioningState?.toLowerCase();
  if (state === 'deleting' || state === 'deleted') return 'Deleting';
  if (state === 'failed' || state === 'canceled') return 'Failed';
  if (state === 'succeeded' || !state) {
    return app.properties?.appResourceId ? 'Ready' : 'NotReady';
  }
  // Any other transitional state (Creating, Accepted, Running, InProgress, etc.)
  return 'Provisioning';
};

const useStyles = makeStyles({
  page: {
    // Outer padding + horizontal layout come from `ProjectLayout.mainContent`.
    // `minHeight: 0; overflow: hidden` keeps the flex chain bounded so the
    // inner `DataTablePanel` can pin its pagination footer to the viewport
    // bottom regardless of row count.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  // Lets the PageSection wrapping the apps table claim the remaining height
  // inside `.page`, so the table panel inside can fill it.
  tableSection: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  viewLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  viewDropdown: {
    minWidth: '120px',
  },
  search: {
    minWidth: '320px',
  },
  metadata: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  emptyStateWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  skeletonCell: {
    flex: 1,
  },
  filterInfoBar: {
    flexShrink: 0,
  },
  errorBanner: {
    flexShrink: 0,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    // Reset native button styles so it looks like the original header text
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
  colName: { width: '63%' },
  colDateModified: { width: '15%' },
  colStatus: { width: '15%' },
  actionsCell: {
    width: '7%',
    whiteSpace: 'nowrap',
    textAlign: 'right',
    paddingRight: tokens.spacingHorizontalM,
  },
  srOnly: visuallyHiddenStyle,
});

interface AppTableRowProps {
  projectName: string;
  app: AutomationAppUIModel;
  viewFilter: string;
  isFavorite: boolean;
  onToggleFavorite: (appName: string, next: boolean) => void;
  getAppLink: (appName: string) => string;
  onSelect: (appName: string) => void;
  onDelete: (appName: string) => void;
}

const AppTableRow = ({ projectName, app, viewFilter, isFavorite, onToggleFavorite, getAppLink, onSelect, onDelete }: AppTableRowProps) => {
  const styles = useStyles();
  const { permission, isLoading } = useAppPermission(app);
  const { data: isProjectAdmin, isLoading: isAdminPermissionsLoading } = useIsProjectOwner(projectName); 

  const hasAccess = useMemo(() => !isAdminPermissionsLoading && permission !== null, [isAdminPermissionsLoading, permission]);
  const appStatus = useMemo(() => getAppStatus(app), [app]);
  const isAppReady = appStatus === 'Ready';
  const shouldViewApp = useMemo(() => {
    if (viewFilter === 'all') return true;
    if (viewFilter === 'Owner') return permission === 'Owner';
    if (viewFilter === 'Shared') return permission === 'Contributor' || permission === 'Reader';
    return false;
  }, [viewFilter, permission]);

  // Filter by access - hide row if it doesn't match the filter
  // Only filter when access data is loaded to avoid flashing
  if (!shouldViewApp && !(isAdminPermissionsLoading || isLoading)) {
    return null;
  }

  // Show skeleton row while loading admin permissions
  if (isAdminPermissionsLoading || isLoading) {
    return (
      <TableRow>
        <TableCell className={styles.colName}>
          <Skeleton><SkeletonItem size={16} className={styles.skeletonCell} /></Skeleton>
        </TableCell>
        <TableCell className={styles.colDateModified}>
          <Skeleton><SkeletonItem size={16} className={styles.skeletonCell} /></Skeleton>
        </TableCell>
        <TableCell className={styles.colStatus}>
          <Skeleton><SkeletonItem size={16} className={styles.skeletonCell} /></Skeleton>
        </TableCell>
        <TableCell className={styles.actionsCell} />
      </TableRow>
    );
  }

  const canDelete = (isProjectAdmin || permission === 'Owner') && appStatus !== 'Deleting';
  const dotStatus: DotStatusKind =
    appStatus === 'Ready' ? 'Active'
    : appStatus === 'Failed' ? 'Error'
    : appStatus === 'Provisioning' ? 'Provisioning'
    : appStatus === 'Deleting' ? 'Deleting'
    : 'Inactive';

  return (
    <TableRow>
      <TableCell className={styles.colName}>
        <EntityNameCell
          icon={<Apps16Regular />}
          name={app.name}
          {...(hasAccess && isAppReady ? { href: getAppLink(app.name) } : {})}
          onClick={() => onSelect(app.name)}
          isFavorite={isFavorite}
          onToggleFavorite={(next) => onToggleFavorite(app.name, next)}
        />
      </TableCell>
      <TableCell className={styles.colDateModified}>
        <Text className={styles.metadata}>
          {formatDateTime(app.properties?.updatedOn, 'full')}
        </Text>
      </TableCell>
      <TableCell className={styles.colStatus}>
        <DotStatusBadge status={dotStatus} />
      </TableCell>
      <TableCell className={styles.actionsCell}>
        {canDelete && (
          <RowActionsMenu ariaLabel={`Actions for ${app.name}`}>
            <MenuItem icon={<DeleteRegular />} onClick={() => onDelete(app.name)}>
              Delete
            </MenuItem>
          </RowActionsMenu>
        )}
      </TableCell>
    </TableRow>
  );
};

export const AppListPage = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();
  const { projectName, locationName, project } = useCurrentProject();
  // Tab title: project-scoped Apps list. Falls back to URL param via
  // useCurrentProject while the project query is still resolving.
  useDocumentTitle(['Apps', projectName]);
  const locationInStore = useAppStore((state) => state.selectedLocation);
  const { tenantId } = useParams<{ tenantId: string }>();
  const { data: apps, isLoading, error, isFetching } = useAppsInProject(projectName ?? '');
  const [search, setSearch] = useState('');
  const [viewFilter, setViewFilter] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const { getOttoToken } = useAuth();
  const hasApps = useMemo(() => !!(apps && apps.length > 0), [apps]);
  const existingAppNames = useMemo(() => (apps ?? []).map((a) => a.name), [apps]);
  const { data: isAdmin, isLoading: isAdminPermissionLoading } = useIsProjectOwner(projectName ?? '');
  const { data: userWithAccess, isLoading: isProjectPermissionsLoading } = usePermission(projectName ?? '', undefined, ['Contributor', 'Author']);
  const isPermissionsLoading = useMemo(() => isAdminPermissionLoading || isProjectPermissionsLoading, [isAdminPermissionLoading, isProjectPermissionsLoading]);
  const userCanCreate = useMemo(() => isAdmin || userWithAccess, [isAdmin, userWithAccess]);

  // Header pill "+ Create new app" navigates here with ?create=1. Wait for
  // permissions to resolve before deciding — otherwise an authorized user
  // could lose the trigger if we strip the flag while perms are still
  // loading. Strip the flag after handling so a reload doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get(CREATE_FLAG_PARAM) !== '1') return;
    if (isPermissionsLoading) return;
    if (userCanCreate) setShowCreateDialog(true);
    const next = new URLSearchParams(searchParams);
    next.delete(CREATE_FLAG_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, userCanCreate, isPermissionsLoading]);

  // Auto-sync onboarding: we're in a project so step 1 is done
  useEffect(() => {
    if (!isLoading) {
      syncOnboardingFromData({ hasProjects: true, hasApps });
    }
  }, [hasApps, isLoading]);

  // Reset error dismissal when error changes (e.g., after retry)
  useEffect(() => {
    if (error) {
      setErrorDismissed(false);
    }
  }, [error]);

  // Track apps that were last seen in the "Deleting" state so that — when
  // the polling query later returns the list without them (because the
  // backend finished tearing the resource down) or with a Failed state —
  // we can surface an appropriate notification. Without this, the row
  // silently disappears and the user has no confirmation. 
  const previouslyDeletingRef = useRef<Set<string>>(new Set());
  // Start time (ms since epoch) of each in-flight delete, used to emit
  // `application.delete.completed` with a duration measurement once the
  // backend confirms the resource is gone. Lets us alarm on regressions
  // of issue #1208 in production.
  const deleteStartTimesRef = useRef<Map<string, number>>(new Map());
  // Reset the tracked refs when the project changes. React Router can swap
  // the `projectName` param without unmounting this page, which would
  // otherwise leak app names from the previous project into the next
  // project's diff and fire spurious success/error notifications.
  useEffect(() => {
    previouslyDeletingRef.current = new Set();
    deleteStartTimesRef.current = new Map();
  }, [projectName]);
  useEffect(() => {
    if (!apps) return;
    const hadDeleting = previouslyDeletingRef.current.size > 0;
    const hasDeletingNow = apps.some((a) => getAppStatus(a) === 'Deleting');
    // Fast path: nothing was deleting before and nothing is now — no diff
    // needed. Avoids allocating Set/Map on every poll for the common case.
    if (!hadDeleting && !hasDeletingNow) return;

    const currentDeleting = new Set<string>();
    const currentByName = new Map<string, AutomationAppUIModel>();
    for (const app of apps) {
      currentByName.set(app.name, app);
      if (getAppStatus(app) === 'Deleting') {
        currentDeleting.add(app.name);
      }
    }

    for (const name of previouslyDeletingRef.current) {
      if (currentDeleting.has(name)) continue;
      const stillPresent = currentByName.get(name);
      const startTime = deleteStartTimesRef.current.get(name);
      const measurements = startTime !== undefined ? { duration_ms: Date.now() - startTime } : undefined;
      if (!stillPresent) {
        useNotificationStore.getState().addNotification({
          type: 'success',
          title: 'Application deleted',
          message: `Application "${name}" was successfully deleted.`,
        });
        trackEvent('application.delete.completed', { projectName: projectName ?? '' }, measurements);
      } else if (getAppStatus(stillPresent) === 'Failed') {
        useNotificationStore.getState().addNotification({
          type: 'error',
          title: 'Delete failed',
          message: `Failed to delete application "${name}". The resource is in a Failed state.`,
        });
        trackEvent('application.delete.failed_during_polling', { projectName: projectName ?? '' }, measurements);
      }
      deleteStartTimesRef.current.delete(name);
    }

    previouslyDeletingRef.current = currentDeleting;
  }, [apps, projectName, trackEvent]);

  // Extract user-friendly error message
  const errorMessage = useMemo(() => {
    if (!error) return null;
    if (error instanceof Error) {
      // Check for common HTTP status codes in the message
      const msg = error.message;
      if (msg.includes('401') || msg.includes('Unauthorized')) {
        return 'Authentication failed. Please sign out and sign back in.';
      }
      if (msg.includes('403') || msg.includes('Forbidden')) {
        return 'You don\'t have permission to view applications in this project.';
      }
      if (msg.includes('404') || msg.includes('Not Found')) {
        return 'Project not found. It may have been deleted or you may not have access.';
      }
      if (msg.includes('500') || msg.includes('Internal Server Error')) {
        return 'A server error occurred. Please try again later.';
      }
      if (msg.includes('network') || msg.includes('Network')) {
        return 'Network error. Please check your connection and try again.';
      }
      return msg;
    }
    return 'An unexpected error occurred while loading applications.';
  }, [error]);

  const showErrorBanner = !!error && !errorDismissed;

  // Only show onboarding when there are truly no apps (unfiltered).
  // If a search or view filter is active, empty results don't mean new user.
  const isAppListFiltered = !!search.trim() || viewFilter !== 'all';
  const showOnboarding = useMemo(
    () => !isLoading && !hasApps && !isAppListFiltered && shouldShowOnboarding(),
    [isLoading, hasApps, isAppListFiltered],
  );

  const viewFilterLabel = useMemo(() => {
    switch (viewFilter) {
      case 'all': return 'All';
      case 'Owner': return 'Your apps';
      case 'Shared': return 'Shared with you';
      default: return viewFilter;
    }
  }, [viewFilter]);

  // Sort state
  type SortColumn = 'name' | 'modified';
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Favorite apps — local-only for now; not yet persisted to the backend.
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set());
  const toggleFavorite = useCallback((appName: string, next: boolean) => {
    setFavorites((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(appName); else updated.delete(appName);
      return updated;
    });
  }, []);

  const handleSort = useCallback((column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const filteredApps = useMemo(() => {
    let result = apps ?? [];
    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter((a) => a.name.toLowerCase().includes(query));
    }
    // Sort
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...result].sort((a, b) => {
      switch (sortColumn) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'modified':
          return dir * ((a.properties?.updatedOn ?? '').localeCompare(b.properties?.updatedOn ?? ''));
        default:
          return 0;
      }
    });
  }, [apps, search, sortColumn, sortDirection]);

  // Pagination state (page size persisted across reloads, page resets on
  // filter / search changes).
  const pagination = usePagination<typeof filteredApps[number]>({
    totalItems: filteredApps.length,
    storageKey: 'otto.appList.pageSize',
    resetDeps: [search, viewFilter, sortColumn, sortDirection],
  });
  // Known limitation: when viewFilter !== 'all', individual rows may
  // return null based on per-row useAppPermission results, so the page
  // can render fewer than pageSize rows and the footer total can drift.
  // Fixing this requires hoisting permission lookups for every app
  // (rules-of-hooks forbid iterating useAppPermission), which is out of
  // scope for this layout pass. Tracked as a follow-up.
  const pagedApps = useMemo(() => pagination.paginate(filteredApps), [pagination, filteredApps]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['appsInProject', projectName?.toLowerCase()] });
  }, [queryClient, projectName]);

  const handleDeleteClick = useCallback((appName: string) => {
    setAppToDelete(appName);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!appToDelete || !projectName) return;
    setIsDeleting(true);
    trackEvent('application.delete.requested', { projectName });
    // Record start time and optimistically mark as "previously deleting" before
    // the await so the transition effect can emit duration_ms + fire the success
    // notification even if the resource disappears before any `Deleting` state
    // is ever observed in the polled list (fast-delete race).
    deleteStartTimesRef.current.set(appToDelete, Date.now());
    previouslyDeletingRef.current.add(appToDelete);
    try {
      const success = await deleteAutomationApp(projectName, appToDelete, getOttoToken);
      if (success) {
        // Drop any Recent Activity entries pointing at this app (and any of
        // its workflows) so the dashboard doesn't show dead links.
        if (tenantId) {
          useRecentWorkStore.getState().removeByScope({
            tenantId,
            project: projectName,
            app: appToDelete,
          });
        }
        handleRefresh();
      } else {
        trackEvent('application.delete.api_failed', {
          projectName,
          error: 'non_ok_response',
        });
        deleteStartTimesRef.current.delete(appToDelete);
        previouslyDeletingRef.current.delete(appToDelete);
        useNotificationStore.getState().addNotification({
          type: 'error',
          title: 'Delete failed',
          message: `Failed to delete application "${appToDelete}". Please try again.`,
        });
      }
    } catch (err) {
      trackEvent('application.delete.api_failed', {
        projectName,
        error: err instanceof Error ? err.message : 'unknown_error',
      });
      deleteStartTimesRef.current.delete(appToDelete);
      previouslyDeletingRef.current.delete(appToDelete);
      useNotificationStore.getState().addNotification({
        type: 'error',
        title: 'Delete failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setAppToDelete(null);
    }
  }, [appToDelete, projectName, getOttoToken, handleRefresh, trackEvent, tenantId]);

  const getAppLink = useCallback((appName: string) => {
    const loc = locationName ?? locationInStore;
    if (loc && projectName) {
      return paths.appWorkflows(loc, projectName, appName, tenantId);
    }
    return `/location/${encodeURIComponent(loc ?? 'unknown')}/projects/${encodeURIComponent(projectName!)}/apps/${encodeURIComponent(appName)}/workflows`;
  }, [locationName, locationInStore, projectName, tenantId]);

  return (
    <div className={styles.page}>
      {projectName && (
        <ProjectOverviewHeader
          name={projectName}
          project={project}
          appCount={apps?.length ?? 0}
        />
      )}

      <PageSection title="Apps" className={styles.tableSection}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <Input
              className={styles.search}
              size="small"
              contentBefore={<SearchRegular />}
              placeholder="Search apps..."
              value={search}
              onChange={(_e, data) => setSearch(data.value)}
              aria-label="Search apps"
            />
            <Label htmlFor="view-filter" className={styles.viewLabel}>View</Label>
            <Dropdown
              id="view-filter"
              size="small"
              className={styles.viewDropdown}
              value={viewFilterLabel}
              selectedOptions={[viewFilter]}
              onOptionSelect={(_e, data) => {
                if (data.optionValue) setViewFilter(data.optionValue);
              }}
            >
              <Option value="all">All</Option>
              <Option value="Owner">Your apps</Option>
              <Option value="Shared">Shared with you</Option>
            </Dropdown>
          </div>
          <div className={styles.toolbarRight}>
            <Button
              appearance="subtle"
              size="small"
              icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
              onClick={handleRefresh}
              disabled={isFetching}
            >
              Refresh
            </Button>
            {!!userCanCreate && !isPermissionsLoading ? (
              <Button
                appearance="primary"
                size="small"
                icon={<AddRegular />}
                disabled={isPermissionsLoading}
                onClick={() => setShowCreateDialog(true)}
              >
                Create app
              </Button>
            ) : null}
          </div>
        </div>

      <CreateAppDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        existingAppNames={existingAppNames}
        onAppCreated={handleRefresh}
      />

      {showErrorBanner && (
        <MessageBar className={styles.errorBanner} intent="error" icon={<ErrorCircleRegular className={styles.errorIcon} />}>
          <MessageBarBody>
            <MessageBarTitle>Failed to load applications</MessageBarTitle>
            {errorMessage}
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="transparent"
              size="small"
              onClick={handleRefresh}
              disabled={isFetching}
            >
              {isFetching ? 'Retrying...' : 'Retry'}
            </Button>
            <Button
              appearance="transparent"
              size="small"
              icon={<DismissRegular />}
              onClick={() => setErrorDismissed(true)}
              aria-label="Dismiss error"
            />
          </MessageBarActions>
        </MessageBar>
      )}

      {showOnboarding && <OnboardingChecklist onAddApplication={() => setShowCreateDialog(true)} />}

      {viewFilter === 'Owner' && !isLoading && (
        <MessageBar className={styles.filterInfoBar} intent="info">
          <MessageBarBody>
            You're viewing applications you own. Use the <strong>View</strong> dropdown to see all applications in this project.
          </MessageBarBody>
        </MessageBar>
      )}

      {isLoading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : filteredApps.length === 0 ? (
        <div className={styles.emptyStateWrapper}>
          <EmptyState
            title="No applications found"
            description={
              search
                ? 'Try a different search term.'
                : userCanCreate
                  ? 'Create an Auto App in this project to start building workflows.'
                  : 'There are no applications in this project yet.'
            }
            {...(!search && !!userCanCreate && !isPermissionsLoading
              ? { action: { label: 'Create application', onClick: () => setShowCreateDialog(true) } }
              : {})}
            {...(!search ? { actionHref: 'https://learn.microsoft.com/azure/logic-apps/' } : {})}
          />
        </div>
      ) : (
        <DataTablePanel
          flushFooter
          footer={
            <TablePaginationFooter
              totalItems={filteredApps.length}
              currentPage={pagination.page}
              pageSize={pagination.pageSize}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          }
        >
          <Table aria-label="Applications table">
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.colName} aria-sort={sortColumn === 'name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" className={styles.sortableHeader} onClick={() => handleSort('name')}>
                    Name {sortColumn === 'name' && (sortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                  </button>
                </TableHeaderCell>
                <TableHeaderCell className={styles.colDateModified} aria-sort={sortColumn === 'modified' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" className={styles.sortableHeader} onClick={() => handleSort('modified')}>
                    Date modified {sortColumn === 'modified' && (sortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                  </button>
                </TableHeaderCell>
                <TableHeaderCell className={styles.colStatus}>Status</TableHeaderCell>
                <TableHeaderCell className={styles.actionsCell}>
                  <span className={styles.srOnly}>Actions</span>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedApps.map((app) => (
                <AppTableRow
                  key={app.name}
                  projectName={projectName!}
                  app={app}
                  viewFilter={viewFilter}
                  isFavorite={favorites.has(app.name)}
                  onToggleFavorite={toggleFavorite}
                  getAppLink={getAppLink}
                  onSelect={(appName) => trackEvent('navigation.app.selected', { appName })}
                  onDelete={handleDeleteClick}
                />
              ))}
            </TableBody>
          </Table>
        </DataTablePanel>
      )}
      </PageSection>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setAppToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        itemType="application"
        itemNames={appToDelete ? [appToDelete] : []}
        isDeleting={isDeleting}
      />
    </div>
  );
};
